/**
 * FilterDropdown — Inline popover for selecting filter values.
 *
 * Renders via createPortal to document.body with fixed positioning below
 * (or above) the clicked filter chip. Contains its own search input and
 * virtualized scrollable suggestion list. Closes on click-outside, Escape,
 * or selection.
 *
 * All filter types support multi-select with checkmarks.
 * Virtual scrolling via @tanstack/react-virtual for 700+ empresa items.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Search } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useTheme } from '../../../contexts/ThemeContext';
import type { MentionType } from '../MentionTypes';

interface FilterDropdownProps {
  filterType: MentionType;
  anchorRect: DOMRect;
  suggestions: { value: string; display: string }[];
  isLoading: boolean;
  onSearch: (query: string) => void;
  onSelect: (value: string) => void;
  onClose: () => void;
  /** Currently selected values — used for multi-select checkmarks (empresa) */
  selectedValues?: string[];
}

const PLACEHOLDER_MAP: Record<string, string> = {
  empresa: 'Buscar empresas...',
  tipo: 'Buscar tipos...',
  fecha: 'Buscar fechas...',
};

const ITEM_HEIGHT = 30; // px per suggestion row

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  filterType,
  anchorRect,
  suggestions,
  isLoading,
  onSearch,
  onSelect,
  onClose,
  selectedValues = [],
}) => {
  const { theme } = useTheme();
  const popoverRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [localSearch, setLocalSearch] = useState('');

  // All filter types support multi-select
  const isMultiSelect = true;

  // Virtual scrolling for large lists (700+ empresas)
  const virtualizer = useVirtualizer({
    count: suggestions.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ITEM_HEIGHT,
    overscan: 10,
  });

  // Stable ref for onSearch — avoids re-triggering debounce when parent re-renders
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  // Debounced search — calls onSearch after 300ms of inactivity.
  // Only depends on localSearch (not onSearch) to avoid re-fire loops.
  // Skips the initial '' because handleFilterClick already loaded suggestions.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      onSearchRef.current(localSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearch]);

  // Autofocus input on mount
  useEffect(() => {
    // Small delay to ensure portal is rendered
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, []);

  // Close on click-outside (same pattern as ChipPopover)
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // setTimeout(0) avoids the opening click from closing the popover
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 0);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Close on window resize
  useEffect(() => {
    const handleResize = () => onClose();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [onClose]);

  // Position: below chip with 4px gap, or above if not enough space
  const dropdownHeight = 400;
  const spaceBelow = window.innerHeight - anchorRect.bottom;
  const openUp = spaceBelow < dropdownHeight && anchorRect.top > spaceBelow;

  const style: React.CSSProperties = {
    position: 'fixed',
    left: `${anchorRect.left}px`,
    width: '280px',
    maxHeight: `${dropdownHeight}px`,
    zIndex: 9999,
    ...(openUp
      ? { bottom: `${window.innerHeight - anchorRect.top + 4}px` }
      : { top: `${anchorRect.bottom + 4}px` }),
  };

  // Suggestions list max height (dropdown minus search header ~46px, minus footer ~30px)
  const listMaxHeight = 340;

  return createPortal(
    <div
      ref={popoverRef}
      className={`flex flex-col rounded-lg border ${theme.colors.border.primary} ${theme.colors.bg.primary} shadow-xl animate-in fade-in slide-in-from-top-1 duration-150`}
      style={style}
    >
      {/* Search input + counter */}
      <div className={`px-2 pt-2 pb-1.5 border-b ${theme.colors.border.primary}`}>
        <div className="relative">
          <Search
            className={`absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 ${theme.colors.text.muted}`}
          />
          <input
            ref={inputRef}
            type="text"
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            placeholder={PLACEHOLDER_MAP[filterType] || 'Buscar...'}
            className={`w-full pl-7 pr-2 py-1 text-xs rounded ${theme.colors.bg.input} border ${theme.colors.border.primary} ${theme.colors.text.primary} focus:outline-none focus:ring-1 focus:ring-nb-accent/50`}
          />
        </div>
        {/* Counter: "738 empresas" or "6 resultados" */}
        {!isLoading && suggestions.length > 0 && (
          <div className={`text-[10px] ${theme.colors.text.muted} mt-1 px-0.5`}>
            {suggestions.length}{' '}
            {localSearch ? 'resultados' : filterType === 'empresa' ? 'empresas' : 'opciones'}
          </div>
        )}
      </div>

      {/* Suggestions list — virtualized */}
      <div
        ref={scrollRef}
        className="overflow-y-auto flex-1 scrollbar-thin"
        style={{ maxHeight: `${listMaxHeight}px` }}
      >
        {isLoading ? (
          <div className={`px-3 py-4 text-center ${theme.colors.text.secondary}`}>
            <div className="animate-spin w-4 h-4 border-2 border-nb-accent border-t-transparent rounded-full mx-auto mb-1.5" />
            <div className="text-[10px]">Cargando...</div>
          </div>
        ) : suggestions.length === 0 ? (
          <div className={`px-3 py-4 text-center ${theme.colors.text.secondary}`}>
            <div className="text-[10px]">{localSearch ? 'Sin resultados' : 'No hay opciones'}</div>
          </div>
        ) : (
          <div
            style={{
              height: `${virtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const item = suggestions[virtualItem.index];
              const isChecked = isMultiSelect && selectedValues.includes(item.value);
              return (
                <button
                  key={item.value}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(item.value);
                  }}
                  className={`flex items-center gap-2 w-full px-2.5 text-left text-xs transition-colors ${
                    isChecked ? 'text-nb-accent font-medium' : `${theme.colors.text.primary}`
                  } hover:${theme.colors.bg.secondary}`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualItem.size}px`,
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  {isMultiSelect && (
                    <span
                      className={`flex-shrink-0 w-3.5 h-3.5 rounded border flex items-center justify-center ${
                        isChecked
                          ? 'bg-nb-accent border-nb-accent text-white'
                          : `${theme.colors.border.primary}`
                      }`}
                    >
                      {isChecked && <Check className="w-2.5 h-2.5" />}
                    </span>
                  )}
                  <span className="truncate">{item.display}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer with count + done button (multi-select only) */}
      {isMultiSelect && selectedValues.length > 0 && (
        <div
          className={`px-2.5 py-1.5 border-t ${theme.colors.border.primary} flex items-center justify-between`}
        >
          <span className={`text-[10px] ${theme.colors.text.secondary}`}>
            {selectedValues.length} seleccionada{selectedValues.length !== 1 ? 's' : ''}
          </span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="text-[10px] font-medium text-nb-accent hover:underline"
          >
            Listo
          </button>
        </div>
      )}
    </div>,
    document.body
  );
};

export default React.memo(FilterDropdown);
